import { useEffect, useState } from "react";
import { api, formatApiError, resizeImageForHero, resizeImageForTicket } from "../lib/api";
import { toast } from "sonner";
import { Save, Printer, Store, ImageIcon, Trash2, Upload, Plus } from "lucide-react";
import { buildReceiptHtml, printReceipt } from "../lib/ticketPrint";
import HomeHighlightsSection from "../components/HomeHighlightsSection";
import HomeHeroSection from "../components/HomeHeroSection";
import { useAuth } from "../context/AuthContext";
import { applyStoreTheme, DEFAULT_STORE_THEME_ID, STORE_THEMES } from "../lib/storeThemes";
import {
    DEFAULT_HOME_HERO,
    DEFAULT_HOME_STATS,
    DEFAULT_MARQUEE_BRANDS,
    normalizeHomeStats,
    normalizeMarqueeBrands,
} from "../lib/homeContent";
import {
    DEFAULT_HOME_HIGHLIGHTS,
    HIGHLIGHT_ICON_OPTIONS,
    emptyHighlight,
    normalizeHomeHighlights,
} from "../lib/homeHighlights";

const emptyForm = {
    business_name: "",
    tagline: "",
    phone: "",
    whatsapp: "",
    email: "",
    address: "",
    city: "",
    map_url: "",
    facebook: "",
    instagram: "",
    about: "",
    ticket_owner_name: "",
    ticket_rfc: "",
    ticket_address: "",
    ticket_phones: "",
    ticket_register_name: "Caja 1",
    ticket_cashier_default: "Predeterminado",
    ticket_customer_default: "PUBLICO EN GENERAL",
    ticket_seller_default: "(Predeterminado)",
    ticket_unit_default: "Pza",
    ticket_prefix: "A",
    ticket_number_width: 8,
    ticket_footer_returns: "",
    ticket_footer_thanks: "",
    ticket_auto_print: true,
    ticket_paper_width_mm: 80,
    ticket_logo: "",
    ticket_logo_height_px: 72,
    home_highlights: DEFAULT_HOME_HIGHLIGHTS,
    home_stats: DEFAULT_HOME_STATS,
    home_marquee_brands: DEFAULT_MARQUEE_BRANDS,
    home_marquee_text: DEFAULT_MARQUEE_BRANDS.join(", "),
    home_hero_badge: DEFAULT_HOME_HERO.badge,
    home_hero_line1: DEFAULT_HOME_HERO.line1,
    home_hero_line2_prefix: DEFAULT_HOME_HERO.line2_prefix,
    home_hero_line2_highlight: DEFAULT_HOME_HERO.line2_highlight,
    home_hero_line3: DEFAULT_HOME_HERO.line3,
    home_hero_subtitle: DEFAULT_HOME_HERO.subtitle,
    home_hero_cta_primary_label: DEFAULT_HOME_HERO.cta_primary_label,
    home_hero_cta_primary_url: DEFAULT_HOME_HERO.cta_primary_url,
    home_hero_cta_secondary_label: DEFAULT_HOME_HERO.cta_secondary_label,
    home_hero_cta_secondary_url: DEFAULT_HOME_HERO.cta_secondary_url,
    home_hero_image_url: DEFAULT_HOME_HERO.image_url,
    home_hero_image_alt: DEFAULT_HOME_HERO.image_alt,
    home_hero_card_label: DEFAULT_HOME_HERO.card_label,
    home_hero_card_text: DEFAULT_HOME_HERO.card_text,
    store_theme_id: DEFAULT_STORE_THEME_ID,
};

export default function AdminSettings() {
    const { user } = useAuth();
    const isSuperAdmin = user?.role === "superadmin";
    const [form, setForm] = useState(emptyForm);
    const [tab, setTab] = useState("ticket");
    const [saving, setSaving] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);

    useEffect(() => {
        api.get("/settings")
            .then((r) => {
                const data = { ...emptyForm, ...r.data };
                data.home_highlights = normalizeHomeHighlights(data.home_highlights);
                data.home_stats = normalizeHomeStats(data.home_stats);
                data.home_marquee_brands = normalizeMarqueeBrands(data.home_marquee_brands);
                data.home_marquee_text = data.home_marquee_brands.join(", ");
                setForm(data);
                applyStoreTheme(data.store_theme_id || DEFAULT_STORE_THEME_ID);
            })
            .catch(() => toast.error("No se pudo cargar la configuración"));
    }, []);

    const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

    const setHighlight = (index, field, value) => {
        setForm((f) => {
            const list = [...(f.home_highlights || [])];
            list[index] = { ...list[index], [field]: value };
            return { ...f, home_highlights: list };
        });
    };

    const addHighlight = () => {
        setForm((f) => ({
            ...f,
            home_highlights: [...(f.home_highlights || []), emptyHighlight()],
        }));
    };

    const removeHighlight = (index) => {
        setForm((f) => ({
            ...f,
            home_highlights: (f.home_highlights || []).filter((_, i) => i !== index),
        }));
    };

    const setStat = (index, field, value) => {
        setForm((f) => {
            const list = [...(f.home_stats || [])];
            list[index] = { ...list[index], [field]: value };
            return { ...f, home_stats: list };
        });
    };

    const addStat = () => {
        setForm((f) => ({
            ...f,
            home_stats: [...(f.home_stats || []), { value: "0", label: "Nueva estadística" }],
        }));
    };

    const removeStat = (index) => {
        setForm((f) => ({
            ...f,
            home_stats: (f.home_stats || []).filter((_, i) => i !== index),
        }));
    };

    const save = async () => {
        setSaving(true);
        try {
            const brands = normalizeMarqueeBrands(form.home_marquee_text || form.home_marquee_brands);
            const payload = {
                ...form,
                home_highlights: normalizeHomeHighlights(form.home_highlights),
                home_stats: normalizeHomeStats(form.home_stats),
                home_marquee_brands: brands,
            };
            delete payload.home_marquee_text;
            const { data } = await api.put("/settings", payload);
            const merged = { ...emptyForm, ...data };
            merged.home_highlights = normalizeHomeHighlights(merged.home_highlights);
            merged.home_stats = normalizeHomeStats(merged.home_stats);
            merged.home_marquee_brands = normalizeMarqueeBrands(merged.home_marquee_brands);
            merged.home_marquee_text = merged.home_marquee_brands.join(", ");
            setForm(merged);
            applyStoreTheme(merged.store_theme_id || DEFAULT_STORE_THEME_ID);
            toast.success("Configuración guardada");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const previewSale = {
        ticket_number: `${form.ticket_prefix || "A"}00001917`,
        created_at: new Date().toISOString(),
        cashier_name: form.ticket_cashier_default,
        register_name: form.ticket_register_name,
        customer_name: form.ticket_customer_default,
        seller_name: form.ticket_seller_default,
        items: [
            {
                name: "Pintura en aerosol negro mate",
                qty: 1,
                unit: form.ticket_unit_default || "Pza",
                unit_price: 73,
                line_total: 73,
            },
        ],
        subtotal: 73,
        total: 73,
        amount_received: 73,
        change_given: 0,
        payment_method: "cash",
    };

    return (
        <div className="p-6 md:p-10 max-w-4xl" data-testid="admin-settings">
            <span className="lz-section-label">Administración</span>
            <h1 className="font-display text-4xl font-bold uppercase mb-2">Configuración</h1>
            <p className="text-sm text-[var(--lz-navy)]/70 mb-4">
                Datos del ticket térmico (80 mm). La impresión usa el diálogo del navegador; asigna tu impresora 3nStar como predeterminada en Windows.
            </p>
            <div className="mb-6 p-4 border-2 border-[var(--lz-orange)]/40 bg-[#fff8f3] text-xs text-[var(--lz-navy)]/80 space-y-1">
                <p className="font-bold uppercase text-[var(--lz-navy)]">Consejos para ticket nítido</p>
                <ul className="list-disc list-inside space-y-0.5">
                    <li>En el diálogo de impresión: escala <strong>100 %</strong>, sin «Ajustar a página».</li>
                    <li>Calidad Normal o Alta (evita modo Borrador).</li>
                    <li>Logo: imagen simple en blanco y negro; las fotos a color se ven tramadas en térmica.</li>
                    <li>Si el texto sigue tenue, sube densidad/oscuridad en las propiedades del driver de la impresora.</li>
                </ul>
            </div>

            <div className="flex gap-2 mb-6 border-b-2 border-[var(--lz-navy)]">
                <button
                    type="button"
                    onClick={() => setTab("ticket")}
                    className={`px-4 py-2 text-xs font-bold uppercase ${tab === "ticket" ? "bg-[var(--lz-orange)] text-white" : "bg-white"}`}
                >
                    <Printer className="w-4 h-4 inline mr-1" /> Ticket / POS
                </button>
                <button
                    type="button"
                    onClick={() => setTab("store")}
                    className={`px-4 py-2 text-xs font-bold uppercase ${tab === "store" ? "bg-[var(--lz-orange)] text-white" : "bg-white"}`}
                >
                    <Store className="w-4 h-4 inline mr-1" /> Tienda pública
                </button>
            </div>

            <div className="border-2 border-[var(--lz-navy)] bg-white p-6 space-y-4">
                {tab === "ticket" && (
                    <>
                        <h2 className="font-display text-xl font-bold uppercase">Logo del ticket</h2>
                        <p className="text-xs text-[var(--lz-navy)]/60">
                            Sube PNG o JPG: se convierte a blanco y negro (384 px) para impresora térmica. Preferible logo simple, no fotos a color. Guarda después de cargar.
                        </p>
                        <div className="flex flex-wrap items-start gap-4 border border-[var(--lz-navy)]/20 p-4">
                            <div className="w-40 h-28 border-2 border-dashed border-[var(--lz-navy)]/30 flex items-center justify-center bg-[#f9fafb] overflow-hidden">
                                {form.ticket_logo ? (
                                    <img src={form.ticket_logo} alt="Logo ticket" className="max-w-full max-h-full object-contain" />
                                ) : (
                                    <ImageIcon className="w-8 h-8 text-[var(--lz-navy)]/25" />
                                )}
                            </div>
                            <div className="flex flex-col gap-2">
                                <label className="lz-btn lz-btn-outline text-xs cursor-pointer inline-flex items-center gap-2 w-fit">
                                    <Upload className="w-4 h-4" />
                                    Subir imagen
                                    <input
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={async (e) => {
                                            const file = e.target.files?.[0];
                                            e.target.value = "";
                                            if (!file) return;
                                            if (file.size > 2 * 1024 * 1024) {
                                                toast.error("Máximo 2 MB; usa una imagen más pequeña");
                                                return;
                                            }
                                            try {
                                                const dataUrl = await resizeImageForTicket(file);
                                                set("ticket_logo", dataUrl);
                                                toast.success("Logo cargado. Pulsa Guardar para aplicarlo.");
                                            } catch (err) {
                                                toast.error(err.message || "Error al procesar imagen");
                                            }
                                        }}
                                    />
                                </label>
                                <button
                                    type="button"
                                    className="lz-btn lz-btn-outline text-xs w-fit"
                                    onClick={() => {
                                        const url = window.prompt("URL de la imagen (https://…)");
                                        if (url?.trim()) set("ticket_logo", url.trim());
                                    }}
                                >
                                    Pegar URL
                                </button>
                                {form.ticket_logo && (
                                    <button
                                        type="button"
                                        className="text-xs text-red-600 font-bold uppercase flex items-center gap-1"
                                        onClick={() => set("ticket_logo", "")}
                                    >
                                        <Trash2 className="w-3 h-3" /> Quitar logo
                                    </button>
                                )}
                            </div>
                        </div>
                        <Field
                            label="Alto máximo del logo (px)"
                            type="number"
                            value={form.ticket_logo_height_px}
                            onChange={(v) => set("ticket_logo_height_px", parseInt(v, 10) || 72)}
                        />

                        <h2 className="font-display text-xl font-bold uppercase pt-4">Encabezado del ticket</h2>
                        <Field label="Nombre en ticket" value={form.business_name} onChange={(v) => set("business_name", v)} />
                        <Field label="Propietario" value={form.ticket_owner_name} onChange={(v) => set("ticket_owner_name", v)} />
                        <Field label="RFC" value={form.ticket_rfc} onChange={(v) => set("ticket_rfc", v)} />
                        <Field label="Dirección (ticket)" value={form.ticket_address} onChange={(v) => set("ticket_address", v)} />
                        <Field label="Teléfonos (ticket)" value={form.ticket_phones} onChange={(v) => set("ticket_phones", v)} />

                        <h2 className="font-display text-xl font-bold uppercase pt-4">Caja y folio</h2>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Nombre de caja" value={form.ticket_register_name} onChange={(v) => set("ticket_register_name", v)} />
                            <Field label="Cajero por defecto" value={form.ticket_cashier_default} onChange={(v) => set("ticket_cashier_default", v)} />
                            <Field label="Cliente por defecto" value={form.ticket_customer_default} onChange={(v) => set("ticket_customer_default", v)} />
                            <Field label="Vendedor por defecto" value={form.ticket_seller_default} onChange={(v) => set("ticket_seller_default", v)} />
                            <Field label="Unidad (ej. Pza)" value={form.ticket_unit_default} onChange={(v) => set("ticket_unit_default", v)} />
                            <Field label="Prefijo folio" value={form.ticket_prefix} onChange={(v) => set("ticket_prefix", v)} />
                            <Field
                                label="Dígitos del folio"
                                type="number"
                                value={form.ticket_number_width}
                                onChange={(v) => set("ticket_number_width", parseInt(v, 10) || 8)}
                            />
                            <Field
                                label="Ancho papel (mm)"
                                type="number"
                                value={form.ticket_paper_width_mm}
                                onChange={(v) => set("ticket_paper_width_mm", parseInt(v, 10) || 80)}
                            />
                        </div>

                        <label className="flex items-center gap-2 text-sm font-bold uppercase cursor-pointer">
                            <input
                                type="checkbox"
                                checked={!!form.ticket_auto_print}
                                onChange={(e) => set("ticket_auto_print", e.target.checked)}
                                className="w-4 h-4"
                            />
                            Imprimir ticket automáticamente al cobrar
                        </label>

                        <h2 className="font-display text-xl font-bold uppercase pt-4">Pie de ticket</h2>
                        <Field
                            label="Política de devoluciones"
                            multiline
                            value={form.ticket_footer_returns}
                            onChange={(v) => set("ticket_footer_returns", v)}
                        />
                        <Field label="Mensaje de despedida" value={form.ticket_footer_thanks} onChange={(v) => set("ticket_footer_thanks", v)} />
                    </>
                )}

                {tab === "store" && (
                    <>
                        <h2 className="font-display text-xl font-bold uppercase">Banner principal (hero)</h2>
                        <p className="text-xs text-[var(--lz-navy)]/60 mb-2">
                            Textos grandes, botones, imagen lateral y cifras de la parte superior de la página de inicio.
                        </p>
                        <div className="border border-[var(--lz-navy)]/20 mb-4 overflow-hidden rounded-sm pointer-events-none">
                            <HomeHeroSection settings={form} />
                        </div>

                        <h2 className="font-display text-xl font-bold uppercase pt-2">Tema de colores</h2>
                        <p className="text-xs text-[var(--lz-navy)]/60">
                            {isSuperAdmin
                                ? "Selecciona una combinación para toda la tienda (7 paletas, destacando azul celeste)."
                                : "Solo el superadministrador puede cambiar la paleta de colores."}
                        </p>
                        <label className="block text-xs font-bold uppercase tracking-widest">
                            Paleta de la tienda
                            <select
                                className="lz-input w-full mt-1 text-sm"
                                value={form.store_theme_id || DEFAULT_STORE_THEME_ID}
                                disabled={!isSuperAdmin}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    set("store_theme_id", val);
                                    applyStoreTheme(val);
                                }}
                            >
                                {STORE_THEMES.map((theme) => (
                                    <option key={theme.id} value={theme.id}>
                                        {theme.name}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <div className="flex flex-wrap items-start gap-4 border border-[var(--lz-navy)]/20 p-4 mb-4">
                            <div className="w-48 h-48 border-2 border-dashed border-[var(--lz-navy)]/30 flex items-center justify-center bg-[#f9fafb] overflow-hidden shrink-0">
                                {form.home_hero_image_url ? (
                                    <img src={form.home_hero_image_url} alt="" className="w-full h-full object-cover" />
                                ) : (
                                    <ImageIcon className="w-8 h-8 text-[var(--lz-navy)]/25" />
                                )}
                            </div>
                            <div className="flex flex-col gap-2 flex-1">
                                <label className="lz-btn lz-btn-outline text-xs cursor-pointer inline-flex items-center gap-2 w-fit">
                                    <Upload className="w-4 h-4" />
                                    Subir imagen del banner
                                    <input
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={async (e) => {
                                            const file = e.target.files?.[0];
                                            e.target.value = "";
                                            if (!file) return;
                                            if (file.size > 3 * 1024 * 1024) {
                                                toast.error("Máximo 3 MB");
                                                return;
                                            }
                                            try {
                                                const url = await resizeImageForHero(file);
                                                set("home_hero_image_url", url);
                                                toast.success("Imagen cargada. Pulsa Guardar.");
                                            } catch (err) {
                                                toast.error(err.message || "Error al procesar");
                                            }
                                        }}
                                    />
                                </label>
                                <Field
                                    label="URL de imagen (o pega enlace)"
                                    value={form.home_hero_image_url}
                                    onChange={(v) => set("home_hero_image_url", v)}
                                />
                                <Field label="Texto alternativo (accesibilidad)" value={form.home_hero_image_alt} onChange={(v) => set("home_hero_image_alt", v)} />
                            </div>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Etiqueta superior (ej. Desde 1992)" value={form.home_hero_badge} onChange={(v) => set("home_hero_badge", v)} />
                            <Field label="Línea 1 del título" value={form.home_hero_line1} onChange={(v) => set("home_hero_line1", v)} />
                            <Field label="Antes de palabra destacada (ej. con)" value={form.home_hero_line2_prefix} onChange={(v) => set("home_hero_line2_prefix", v)} />
                            <Field label="Palabra destacada (naranja)" value={form.home_hero_line2_highlight} onChange={(v) => set("home_hero_line2_highlight", v)} />
                            <Field label="Línea 3 del título" value={form.home_hero_line3} onChange={(v) => set("home_hero_line3", v)} />
                        </div>
                        <Field label="Párrafo debajo del título" multiline value={form.home_hero_subtitle} onChange={(v) => set("home_hero_subtitle", v)} />
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Botón principal — texto" value={form.home_hero_cta_primary_label} onChange={(v) => set("home_hero_cta_primary_label", v)} />
                            <Field label="Botón principal — enlace" value={form.home_hero_cta_primary_url} onChange={(v) => set("home_hero_cta_primary_url", v)} />
                            <Field label="Botón secundario — texto" value={form.home_hero_cta_secondary_label} onChange={(v) => set("home_hero_cta_secondary_label", v)} />
                            <Field label="Botón secundario — enlace" value={form.home_hero_cta_secondary_url} onChange={(v) => set("home_hero_cta_secondary_url", v)} />
                            <Field label="Etiqueta caja naranja (imagen)" value={form.home_hero_card_label} onChange={(v) => set("home_hero_card_label", v)} />
                            <Field label="Texto caja naranja (marcas)" value={form.home_hero_card_text} onChange={(v) => set("home_hero_card_text", v)} />
                        </div>

                        <h2 className="font-display text-xl font-bold uppercase pt-6 border-t border-[var(--lz-navy)]/10">
                            Cifras bajo el banner
                        </h2>
                        <p className="text-xs text-[var(--lz-navy)]/60 mb-2">32+, 5,000+, etc. Puedes agregar o quitar columnas.</p>
                        <div className="space-y-3">
                            {(form.home_stats || []).map((s, idx) => (
                                <div key={idx} className="flex flex-wrap gap-2 items-end border border-[var(--lz-navy)]/15 p-3">
                                    <Field label={`Valor ${idx + 1}`} value={s.value} onChange={(v) => setStat(idx, "value", v)} />
                                    <Field label="Etiqueta" value={s.label} onChange={(v) => setStat(idx, "label", v)} />
                                    <button type="button" onClick={() => removeStat(idx)} className="text-xs text-red-600 font-bold uppercase mb-1">
                                        Eliminar
                                    </button>
                                </div>
                            ))}
                            <button type="button" onClick={addStat} className="lz-btn lz-btn-outline text-xs">
                                <Plus className="w-4 h-4" /> Agregar cifra
                            </button>
                        </div>

                        <Field
                            label="Marcas en franja naranja (separadas por coma)"
                            multiline
                            value={form.home_marquee_text}
                            onChange={(v) => set("home_marquee_text", v)}
                        />

                        <h2 className="font-display text-xl font-bold uppercase pt-6 border-t border-[var(--lz-navy)]/10">
                            Bloques bajo la franja naranja
                        </h2>
                        <p className="text-xs text-[var(--lz-navy)]/60 mb-2">
                            Columnas de envío, garantía, etc. Agrega o elimina las que necesites.
                        </p>
                        <div className="border border-[var(--lz-navy)]/20 p-3 mb-4 overflow-hidden">
                            <HomeHighlightsSection highlights={form.home_highlights} />
                        </div>
                        <div className="space-y-4">
                            {(form.home_highlights || []).map((h, idx) => (
                                <div key={idx} className="border border-[var(--lz-navy)]/15 p-4 space-y-3">
                                    <div className="flex justify-between items-center">
                                        <div className="text-xs font-bold uppercase text-[var(--lz-orange)]">Columna {idx + 1}</div>
                                        <button type="button" onClick={() => removeHighlight(idx)} className="text-xs text-red-600 font-bold uppercase">
                                            Eliminar columna
                                        </button>
                                    </div>
                                    <label className="block text-xs font-bold uppercase tracking-widest">
                                        Icono
                                        <select
                                            value={h.icon || "truck"}
                                            onChange={(e) => setHighlight(idx, "icon", e.target.value)}
                                            className="lz-input w-full mt-1 text-sm"
                                        >
                                            {HIGHLIGHT_ICON_OPTIONS.map((opt) => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                    <Field label="Título" value={h.title || ""} onChange={(v) => setHighlight(idx, "title", v)} />
                                    <Field label="Descripción" value={h.description || ""} onChange={(v) => setHighlight(idx, "description", v)} />
                                </div>
                            ))}
                            <button type="button" onClick={addHighlight} className="lz-btn lz-btn-outline text-xs">
                                <Plus className="w-4 h-4" /> Agregar columna
                            </button>
                        </div>

                        <h2 className="font-display text-xl font-bold uppercase pt-6 border-t border-[var(--lz-navy)]/10">
                            Datos generales
                        </h2>
                        <Field label="Nombre comercial" value={form.business_name} onChange={(v) => set("business_name", v)} />
                        <Field label="Eslogan" value={form.tagline} onChange={(v) => set("tagline", v)} />
                        <Field label="Teléfono" value={form.phone} onChange={(v) => set("phone", v)} />
                        <Field label="WhatsApp" value={form.whatsapp} onChange={(v) => set("whatsapp", v)} />
                        <Field label="Email" value={form.email} onChange={(v) => set("email", v)} />
                        <Field label="Dirección" value={form.address} onChange={(v) => set("address", v)} />
                        <Field label="Ciudad" value={form.city} onChange={(v) => set("city", v)} />
                        <Field label="Acerca de" multiline value={form.about} onChange={(v) => set("about", v)} />
                    </>
                )}
            </div>

            <div className="flex flex-wrap gap-3 mt-6">
                <button type="button" onClick={save} disabled={saving} className="lz-btn lz-btn-primary" data-testid="settings-save">
                    <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar"}
                </button>
                {tab === "ticket" && (
                    <>
                        <button type="button" onClick={() => setPreviewOpen(true)} className="lz-btn lz-btn-outline">
                            Vista previa
                        </button>
                        <button
                            type="button"
                            onClick={async () => {
                                try {
                                    await printReceipt(previewSale, form);
                                } catch (e) {
                                    toast.error(e.message || "Error al imprimir");
                                }
                            }}
                            className="lz-btn lz-btn-outline"
                        >
                            <Printer className="w-4 h-4" /> Imprimir ejemplo
                        </button>
                    </>
                )}
            </div>

            {previewOpen && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setPreviewOpen(false)}>
                    <div
                        className="bg-white border-2 border-[var(--lz-navy)] max-w-md w-full max-h-[85vh] overflow-auto p-4 shadow-[8px_4px_0_var(--lz-navy)]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <iframe
                            title="Vista previa ticket"
                            srcDoc={buildReceiptHtml(previewSale, form)}
                            className="w-full min-h-[480px] border border-[var(--lz-navy)]/20"
                        />
                        <button type="button" className="lz-btn lz-btn-outline w-full mt-4" onClick={() => setPreviewOpen(false)}>
                            Cerrar
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function Field({ label, value, onChange, type = "text", multiline = false }) {
    const cls = "lz-input w-full mt-1 font-mono text-sm";
    return (
        <label className="block text-xs font-bold uppercase tracking-widest">
            {label}
            {multiline ? (
                <textarea rows={3} value={value} onChange={(e) => onChange(e.target.value)} className={cls} />
            ) : (
                <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className={cls} />
            )}
        </label>
    );
}
